import type { ReactElement } from 'react';

import classNames from 'classnames';

import ErrorBoundary from '../../components/error_boundary/error_boundary';
import type { ObjectWithId } from '../../types/common';
import { ui } from '../../utils/helpers';
import type { FilesGridColumn } from './types';

import styles from './asset_files.module.css';

type Props<Item extends ObjectWithId> = {
  items: Item[];
  columns: FilesGridColumn<Item>[];
  selectedIds: string[];
  uiKey: string;
};

function FilesGrid<Item extends ObjectWithId>(
  props: Props<Item>,
): ReactElement {
  const { items, columns, selectedIds, uiKey } = props;

  return (
    <div role="grid" {...ui({ table: uiKey })} className={styles.grid}>
      {items.map((item) => {
        const isSelected = selectedIds.includes(item.id);
        return (
          <ErrorBoundary key={item.id}>
            <div
              role="row"
              tabIndex={-1}
              data-id={item.id}
              aria-selected={isSelected}
              className={classNames(styles.grid_row, {
                [styles.grid_row_selected]: isSelected,
              })}
            >
              {columns.map((column) => {
                const CellComponent = column.component;
                return (
                  <div
                    role="gridcell"
                    tabIndex={-1}
                    key={column.name}
                    className={classNames(styles.grid_cell, {
                      [styles.grid_cell_full]: column.fullWidth,
                    })}
                  >
                    <CellComponent item={item} />
                  </div>
                );
              })}
            </div>
          </ErrorBoundary>
        );
      })}
    </div>
  );
}

export default FilesGrid;
